/**
 * Shared shell for the static legal pages (Privacy / Terms / Offer).
 *
 * Renders a centred reading column with a header (back-to-home link
 * + language switcher), the page body, and the same footer
 * navigation as the landing page so links between Privacy / Terms /
 * Offer always work — even when the user lands directly on one.
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import LanguageSwitcher from "../../components/LanguageSwitcher.jsx";

export default function LegalLayout({ title, lastUpdated, children }) {
  const { t } = useTranslation();

  // Set the browser tab title — legal pages are linked from external
  // emails / receipts, so the tab name matters.
  useEffect(() => {
    if (title) document.title = `${title} · SEOGeeks AI Visibility`;
  }, [title]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/"
            className="mono text-xs tracking-widest text-brand-700 hover:text-brand-900"
          >
            ← SEOGEEKS / AI VISIBILITY
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1">
        <article className="max-w-3xl mx-auto px-6 py-10 prose prose-slate prose-sm sm:prose-base">
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-2">
            {title}
          </h1>
          {lastUpdated && (
            <p className="text-xs text-slate-500 mb-6">{lastUpdated}</p>
          )}
          {children}
        </article>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-6 text-xs text-slate-500 flex flex-wrap justify-between gap-3">
          <span>© {new Date().getFullYear()} SEOGeeks · AI Visibility</span>
          <div className="space-x-3">
            <Link to="/privacy" className="hover:text-slate-700">
              {t("legal.privacyShort")}
            </Link>
            <Link to="/terms" className="hover:text-slate-700">
              {t("legal.termsShort")}
            </Link>
            <Link to="/offer" className="hover:text-slate-700">
              {t("legal.offerShort")}
            </Link>
            <Link to="/" className="hover:text-slate-700">
              {t("legal.backHome")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
